// The therapist session cycle, and the three steps pg_cron owns (0242).
//
// ⚠ WHY THIS SCREEN EXISTS. Creating a cycle, opening it to priority and
// opening it to everyone were reachable from pg_cron and nothing else. A missed
// run had no recovery path and, worse, no symptom: therapists simply cannot
// pick days, which reads as a hub with no availability rather than as a job
// that did not fire. CLAUDE.md records the same shape from the pilot roster —
// "the symptom is not an error, it is every window reporting no availability".
//
// ⚠ NEITHER BUTTON IS A SUCCESS/FAILURE FLAG. `create` is idempotent on the
// service week and both openers are time-gated inside the SQL, so a press can
// legitimately change nothing. The result copy below reads `created` / `opened`
// and says which happened. Reporting every 200 as "done" would tell an operator
// a cycle exists when none was built.
//
// Finalization is deliberately absent — it writes the immutable
// `therapist_final_counts` that gates rider supply, so it stays cron-only.

import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldLabel } from "@bednbody/ui/components/field";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useState } from "react";
import {
  type TherapistCycleStatus,
  type TherapistCycleStep,
  useRunTherapistCycleStep,
  useTherapistCycles,
} from "./therapist-sessions.hooks";

const MANILA = "Asia/Manila";

function manilaDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-PH", {
    timeZone: MANILA,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function manilaDate(isoDate: string): string {
  // A bare YYYY-MM-DD is parsed as UTC midnight; render it as the calendar date
  // it is rather than shifting it into the previous day.
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATUS_TONE: Record<TherapistCycleStatus["status"], "muted" | "info" | "success"> = {
  not_open: "muted",
  priority_open: "info",
  regular_open: "info",
  securing_closed: "muted",
  finalized: "success",
};

const STATUS_LABEL: Record<TherapistCycleStatus["status"], string> = {
  not_open: "Not open",
  priority_open: "Priority open",
  regular_open: "Open to all",
  securing_closed: "Securing closed",
  finalized: "Finalized",
};

const STEPS: { step: TherapistCycleStep; label: string; blurb: string }[] = [
  {
    step: "create",
    label: "Create next cycle",
    blurb:
      "Builds next service week and enrols every active therapist with a hub. Safe to press twice — it returns the same cycle.",
  },
  {
    step: "open-priority",
    label: "Open to priority",
    blurb: "Only takes effect once the priority window has started. Before that it does nothing.",
  },
  {
    step: "open-regular",
    label: "Open to all",
    blurb: "Only takes effect once the regular window has started. Before that it does nothing.",
  },
];

export function CycleStatusCard() {
  const { data: cycles, isPending, isError } = useTherapistCycles();
  const run = useRunTherapistCycleStep();
  const [pendingStep, setPendingStep] = useState<TherapistCycleStep | null>(null);
  const [reason, setReason] = useState("");

  const current = cycles?.[0] ?? null;
  const active = STEPS.find((s) => s.step === pendingStep) ?? null;

  const close = () => {
    setPendingStep(null);
    setReason("");
    run.reset();
  };

  const outcome = run.data
    ? run.data.created === true
      ? `Created the week of ${run.data.weekStartDate ? manilaDate(run.data.weekStartDate) : "—"}.`
      : run.data.created === false
        ? "That week already existed — membership was refreshed, no new cycle."
        : run.data.opened && run.data.opened > 0
          ? `Opened ${run.data.opened} cycle${run.data.opened === 1 ? "" : "s"}.`
          : "Nothing changed — the opening window hasn't started yet."
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle><h2 className="type-title-subsection">Session cycle</h2></CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError ? (
          // ⚠ NOT an empty state. "No cycle" and "couldn't load" look identical
          // to an operator and mean opposite things.
          <p className="text-sm text-destructive">
            Couldn&rsquo;t load the cycle &mdash; check your connection and try again.
          </p>
        ) : isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : !current ? (
          <p className="text-sm text-muted-foreground">
            No cycle has been created yet. The Sunday job builds one automatically; use Create next
            cycle if it didn&rsquo;t run.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-medium">Week of {manilaDate(current.week_start_date)}</p>
              <Badge variant={STATUS_TONE[current.status]}>{STATUS_LABEL[current.status]}</Badge>
            </div>

            <dl className="grid grid-cols-2 gap-3 text-sm @2xl/canvas:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Therapists enrolled</dt>
                <dd className="font-medium">{current.members}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Have picked days</dt>
                <dd className="font-medium">
                  {current.secured} of {current.members}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Sessions</dt>
                <dd className="font-medium">{current.sessions}</dd>
              </div>
            </dl>

            <dl className="grid grid-cols-1 gap-2 text-sm @2xl/canvas:grid-cols-2">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Priority opens</dt>
                <dd>{manilaDateTime(current.priority_opens_at)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Opens to all</dt>
                <dd>{manilaDateTime(current.regular_opens_at)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Approval cutoff</dt>
                <dd>{manilaDateTime(current.approval_cutoff_at)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Securing cutoff</dt>
                <dd>{manilaDateTime(current.securing_cutoff_at)}</dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-2 border-t border-border pt-3">
              {STEPS.map((s) => (
                <Button
                  key={s.step}
                  variant="outline"
                  onClick={() => {
                    run.reset();
                    setReason("");
                    setPendingStep(s.step);
                  }}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              These normally run on a schedule. Use them only when a scheduled run was missed
              &mdash; every press is recorded in the audit log.
            </p>
          </>
        )}
      </CardContent>

      <Dialog open={pendingStep !== null} onOpenChange={(open) => (open ? null : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{active?.label}</DialogTitle>
            <DialogDescription>{active?.blurb}</DialogDescription>
          </DialogHeader>

          {outcome ? (
            <p className="text-sm">{outcome}</p>
          ) : (
            <Field>
              <FieldLabel htmlFor="cycle-step-reason">Why are you running this by hand?</FieldLabel>
              <Textarea
                id="cycle-step-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. the Sunday job didn't fire"
                rows={3}
              />
            </Field>
          )}

          {run.isError ? (
            <p className="text-sm text-destructive">
              {run.error instanceof Error ? run.error.message : "That didn't go through."}
            </p>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={close}>
              {outcome ? "Done" : "Cancel"}
            </Button>
            {outcome ? null : (
              <Button
                disabled={reason.trim().length === 0 || run.isPending}
                onClick={() => {
                  if (pendingStep) run.mutate({ step: pendingStep, reason: reason.trim() });
                }}
              >
                {run.isPending ? "Running…" : "Run it"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
